import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Category } from '~/models/Category'
import { Site2 } from '~/models/Site'
import { Block2 } from '~/models/Block'
import { Destination2 } from '~/models/Destination'
import Product, { Activity } from '~/models/Product'
import { Guide2 } from '~/models/Guide'
import { SiteInfo2 } from '~/models/SiteInfo'
import { Spot2 } from '~/models/Spot'

export const useSiteStore = defineStore('site', () => {
	const sites = ref<Record<string, Site2>>({})
	const sitesInfo = ref<Record<string, SiteInfo2[]>>({})
	const guideCategories = ref<Record<string, Category[]>>({})
	const guidesFromCategory = reactive<Record<string, Record<string, Guide2[]>>>({})
	const siteProducts = ref<Record<string, Block2<Product>[]>>({})
	const recommendedSpots = ref<Record<string, Spot2[]>>({})
	const sitesDestinationBlocks = ref<Record<string, Block2<Destination2>[]>>({})
	const trips = ref<Record<string, Product[]>>({})
	const activities = ref<Record<string, Activity[]>>({})

	const setSite = (newSite: Site2) => {
		sites.value[newSite.id] = newSite
	}

	const getSite = (siteId: string) => {
		return sites.value[siteId]
	}

	const setGuideCategories = (siteId: string, categories: Category[]) => {
		guideCategories.value[siteId] = categories
	}

	const getGuideCategories = (siteId: string): Category[] => {
		return guideCategories.value[siteId]
	}

	const setSiteProducts = (siteId: string, products: Block2<Product>[]) => {
		siteProducts.value[siteId] = products
	}

	const getRecommendedSpots = (siteId: string) => {
		return recommendedSpots.value[siteId]
	}

	const setRecommendedSpots = (siteId: string, spots: Spot2[]) => {
		recommendedSpots.value[siteId] = spots
	}

	const getSiteProducts = (siteId: string) => {
		return siteProducts.value[siteId]
	}

	const setSiteDestinationBlocks = (siteId: string, destinationBlocks: Block2<Destination2>[]) => {
		sitesDestinationBlocks.value[siteId] = destinationBlocks
	}

	const getSiteDestinationBlocks = (siteId: string) => {
		return sitesDestinationBlocks.value[siteId]
	}

	const getGuidesFrom = (siteId: string, categoryId: string = 'all'): Guide2[] | undefined => {
		const siteCategories = guidesFromCategory[siteId]
		if (!siteCategories) return
		const guides = siteCategories[categoryId]
		return guides
	}

	const setGuidesFrom = (siteId: string, guides: Guide2[], categoryId: string = 'all') => {
		const categories = guidesFromCategory[siteId]
		if (!categories) guidesFromCategory[siteId] = { [categoryId]: [] }
		guidesFromCategory[siteId][categoryId] = guides
	}

	const setSiteInfo = (siteId: string, newInfo: SiteInfo2[]) => {
		sitesInfo.value[siteId] = newInfo
	}

	const getSiteInfo = (siteId: string): SiteInfo2[] => {
		return sitesInfo.value[siteId]
	}

	const getTrips = (siteId: string) => {
		return trips.value[siteId]
	}

	const setTrips = (siteId: string, newTrips: Product[]) => {
		trips.value[siteId] = newTrips
	}

	const getActivities = (siteId: string) => {
		return activities.value[siteId]
	}

	const setActivities = (siteId: string, newActivities: Activity[]) => {
		activities.value[siteId] = newActivities
	}

	return {
		sites,
		guideCategories,
		setSite,
		getSite,
		setGuideCategories,
		getGuideCategories,
		setSiteProducts,
		getSiteProducts,
		setSiteDestinationBlocks,
		getSiteDestinationBlocks,
		setGuidesFrom,
		getGuidesFrom,
		setSiteInfo,
		getSiteInfo,
		setRecommendedSpots,
		getRecommendedSpots,
		getTrips,
		setTrips,
		getActivities,
		setActivities,
	}
})
